<template>
  <div v-if="panelVisible" class="ai-panel" :class="{ 'ai-panel--minimized': minimized }">

    <!-- Header -->
    <div class="ai-panel-header" @click.self="minimized = !minimized">
      <div class="ai-header-left" @click="minimized = !minimized">
        <span class="ai-pulse-dot"></span>
        <span class="ai-header-title">AI Agent</span>
      </div>
      <div class="ai-header-actions">
        <v-icon size="15" color="#6366f1" style="cursor:pointer" @click.stop="minimized = !minimized">
          {{ minimized ? 'mdi-chevron-down' : 'mdi-chevron-up' }}
        </v-icon>
      </div>
    </div>

    <!-- Body -->
    <div v-show="!minimized" class="ai-panel-body">

      <!-- Demographic -->
      <div class="ai-section">
        <div class="ai-section-header" @click="toggle('demographic_info')">
          <div class="ai-section-title">
            <v-icon size="14" class="mr-1" color="#6366f1">mdi-account-outline</v-icon>
            Patient Info
          </div>
          <div class="ai-section-meta">
            <span class="ai-count" :class="demographicFilled > 0 ? 'ai-count--filled' : ''">
              {{ demographicFilled }}/9
            </span>
            <div class="ai-mini-bar-wrap">
              <div class="ai-mini-bar ai-mini-bar--demo" :style="{ width: demographicProgress + '%' }"></div>
            </div>
            <v-icon size="13" color="#94a3b8">{{ expanded.demographic_info ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
        </div>
        <transition name="ai-expand">
          <div v-show="expanded.demographic_info" class="ai-fields">
            <div v-for="f in fields('demographic_info')" :key="f.key"
              class="ai-field-row" :class="{ 'ai-field-row--collected': f.collected }">
              <v-icon v-if="f.collected" size="13" color="#22c55e" class="ai-field-icon">mdi-check-circle</v-icon>
              <span v-else class="ai-listen-dot"></span>
              <span class="ai-field-label">{{ f.label }}</span>
              <span v-if="f.collected" class="ai-field-value">{{ f.value }}</span>
              <span v-else class="ai-field-hint">listening…</span>
            </div>
          </div>
        </transition>
      </div>

      <!-- Insurance -->
      <div class="ai-section">
        <div class="ai-section-header" @click="toggle('insurance_info')">
          <div class="ai-section-title">
            <v-icon size="14" class="mr-1" color="#0ea5e9">mdi-shield-check-outline</v-icon>
            Insurance
          </div>
          <div class="ai-section-meta">
            <span v-if="noInsurance" class="ai-no-ins">No Insurance</span>
            <template v-else>
              <span class="ai-count" :class="insuranceFilled > 0 ? 'ai-count--filled' : ''">
                {{ insuranceFilled }}/6
              </span>
              <div class="ai-mini-bar-wrap">
                <div class="ai-mini-bar ai-mini-bar--ins" :style="{ width: insuranceProgress + '%' }"></div>
              </div>
            </template>
            <v-icon size="13" color="#94a3b8">{{ expanded.insurance_info ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
        </div>
        <transition name="ai-expand">
          <div v-show="expanded.insurance_info" class="ai-fields">
            <div v-if="noInsurance" class="ai-no-ins-row">
              <v-icon size="13" color="#f59e0b">mdi-information-outline</v-icon>
              <span>Patient has no insurance</span>
            </div>
            <template v-else>
              <div v-for="f in fields('insurance_info')" :key="f.key"
                class="ai-field-row" :class="{ 'ai-field-row--collected': f.collected }">
                <v-icon v-if="f.collected" size="13" color="#22c55e" class="ai-field-icon">mdi-check-circle</v-icon>
                <span v-else class="ai-listen-dot ai-listen-dot--ins"></span>
                <span class="ai-field-label">{{ f.label }}</span>
                <span v-if="f.collected" class="ai-field-value">{{ f.value }}</span>
                <span v-else class="ai-field-hint">listening…</span>
              </div>
            </template>
          </div>
        </transition>
      </div>

      <!-- Clinical -->
      <div class="ai-section ai-section--last">
        <div class="ai-section-header" @click="toggle('clinical_info')">
          <div class="ai-section-title">
            <v-icon size="14" class="mr-1" color="#10b981">mdi-stethoscope</v-icon>
            Reason for Visit
          </div>
          <div class="ai-section-meta">
            <span class="ai-count" :class="clinicalFilled > 0 ? 'ai-count--filled' : ''">
              {{ clinicalFilled }}/1
            </span>
            <div class="ai-mini-bar-wrap">
              <div class="ai-mini-bar ai-mini-bar--clin" :style="{ width: clinicalProgress + '%' }"></div>
            </div>
            <v-icon size="13" color="#94a3b8">{{ expanded.clinical_info ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </div>
        </div>
        <transition name="ai-expand">
          <div v-show="expanded.clinical_info" class="ai-fields">
            <div v-for="f in fields('clinical_info')" :key="f.key"
              class="ai-field-row" :class="{ 'ai-field-row--collected': f.collected }">
              <v-icon v-if="f.collected" size="13" color="#22c55e" class="ai-field-icon">mdi-check-circle</v-icon>
              <span v-else class="ai-listen-dot ai-listen-dot--clin"></span>
              <span class="ai-field-label">{{ f.label }}</span>
              <span v-if="f.collected" class="ai-field-value">{{ f.value }}</span>
              <span v-else class="ai-field-hint">listening…</span>
            </div>
          </div>
        </transition>
      </div>

    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

const DEMOGRAPHIC_FIELDS = [
  { key: 'first_name',             label: 'First Name' },
  { key: 'last_name',              label: 'Last Name' },
  { key: 'date_of_birth',          label: 'Date of Birth' },
  { key: 'phone_number',           label: 'Phone' },
  { key: 'email_address',          label: 'Email' },
  { key: 'home_address',           label: 'Home Address' },
  { key: 'zip',                    label: 'Zip' },
  { key: 'social_security_number', label: 'SSN' },
  { key: 'referral_source',        label: 'Referral Source' },
];
const INSURANCE_FIELDS = [
  { key: 'insurance_company_name', label: 'Insurance Company' },
  { key: 'employer',               label: 'Employer' },
  { key: 'group_name',             label: 'Group Name' },
  { key: 'group_number',           label: 'Group Number' },
  { key: 'subscriber_id',          label: 'Subscriber ID' },
  { key: 'effective_date',         label: 'Effective Date' },
];
const CLINICAL_FIELDS = [
  { key: 'reason_for_visit', label: 'Reason for Visit' },
];

export default {
  name: 'AIAgentPanel',
  data() {
    return {
      localPatientInfo: {},
      expanded: { demographic_info: true, insurance_info: false, clinical_info: true },
    };
  },
  computed: {
    ...mapGetters('openAI', ['getPatientInfoByCall']),
    ...mapGetters('plivo', ['getCallBarDisplay', 'getCurrConferenceName']),

    activePatientInfo() {
      return this.getPatientInfoByCall?.[this.getCurrConferenceName] || {};
    },

    panelVisible: {
      get() { return this.$store.getters['openAI/getAIPanelVisible']; },
      set(val) { this.$store.commit('openAI/setAIPanelVisible', val); },
    },
    minimized: {
      get() { return this.$store.getters['openAI/getAIPanelMinimized']; },
      set(val) { this.$store.commit('openAI/setAIPanelMinimized', val); },
    },

    noInsurance() {
      return this.localPatientInfo.insurance_info?.has_insurance === false;
    },
    demographicFilled() {
      const info = this.localPatientInfo.demographic_info || {};
      return DEMOGRAPHIC_FIELDS.filter(f => info[f.key] && info[f.key] !== '').length;
    },
    demographicProgress() {
      return Math.round((this.demographicFilled / DEMOGRAPHIC_FIELDS.length) * 100);
    },
    insuranceFilled() {
      const info = this.localPatientInfo.insurance_info || {};
      return INSURANCE_FIELDS.filter(f => info[f.key] && info[f.key] !== '').length;
    },
    insuranceProgress() {
      return Math.round((this.insuranceFilled / INSURANCE_FIELDS.length) * 100);
    },
    clinicalFilled() {
      const info = this.localPatientInfo.clinical_info || {};
      return CLINICAL_FIELDS.filter(f => info[f.key] && info[f.key] !== '').length;
    },
    clinicalProgress() {
      return Math.round((this.clinicalFilled / CLINICAL_FIELDS.length) * 100);
    },
  },
  watch: {
    activePatientInfo: {
      deep: true,
      handler(newInfo) {
        if (!newInfo || newInfo.is_patient_registered !== false) return;
        const display = this.getCallBarDisplay;
        if (display !== 'inCall' && display !== 'addToCall') return;
        this.localPatientInfo = JSON.parse(JSON.stringify(newInfo));
      },
    },
    getCallBarDisplay(newVal, oldVal) {
      const wasInCall = oldVal === 'inCall' || oldVal === 'addToCall';
      const leftCall = newVal === 'default' || newVal === 'calling' || newVal === 'incomingCall';
      if (wasInCall && leftCall) {
        this.$store.commit('openAI/setAIPanelVisible', false);
        this.$store.commit('openAI/setAIPanelMinimized', false);
        this.$store.commit('openAI/setAINewPatient', false);
        this.localPatientInfo = {};
        return;
      }
      // If data arrived while display was blocked (e.g. 'incomingCall'), apply it now
      const nowInCall = newVal === 'inCall' || newVal === 'addToCall';
      if (nowInCall) {
        const info = this.activePatientInfo;
        if (info && info.is_patient_registered === false) {
          this.localPatientInfo = JSON.parse(JSON.stringify(info));
        }
      }
    },
  },
  methods: {
    toggle(section) {
      this.expanded[section] = !this.expanded[section];
    },
    fields(section) {
      const map = {
        demographic_info: DEMOGRAPHIC_FIELDS,
        insurance_info: INSURANCE_FIELDS,
        clinical_info: CLINICAL_FIELDS,
      };
      const info = this.localPatientInfo[section] || {};
      return map[section].map(f => ({
        key:       f.key,
        label:     f.label,
        collected: !!(info[f.key] !== undefined && info[f.key] !== '' && info[f.key] !== null),
        value:     info[f.key] ?? '',
      }));
    },
  },
};
</script>

<style scoped>
/* ── Panel shell ─────────────────────────────────────────────── */
.ai-panel {
  position: fixed;
  top: 64px;
  right: 16px;
  width: 285px;
  z-index: 1300;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 4px 24px rgba(99,102,241,0.13), 0 1px 4px rgba(0,0,0,0.06);
  border: 1.5px solid #e0e7ff;
  overflow: hidden;
  font-family: inherit;
}

/* ── Header ──────────────────────────────────────────────────── */
.ai-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  background: linear-gradient(90deg, #f4f3fd 60%, #ede9fe 100%);
  border-bottom: 1px solid #e0e7ff;
  user-select: none;
}
.ai-header-left  { display: flex; align-items: center; gap: 7px; }
.ai-header-actions { display: flex; align-items: center; gap: 6px; }
.ai-header-title { font-size: 13px; font-weight: 600; color: #4338ca; }

/* Pulsing dot */
.ai-pulse-dot {
  width: 9px; height: 9px; border-radius: 50%;
  background: #6366f1; display: inline-block;
  animation: pulse-ring 1.8s ease-out infinite;
}
@keyframes pulse-ring {
  0%   { box-shadow: 0 0 0 0 rgba(99,102,241,0.5); }
  60%  { box-shadow: 0 0 0 6px rgba(99,102,241,0); }
  100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); }
}

/* ── Body ────────────────────────────────────────────────────── */
.ai-panel-body { padding: 6px 0 4px; }

/* ── Sections ────────────────────────────────────────────────── */
.ai-section { border-bottom: 1px solid #f1f5f9; }
.ai-section--last { border-bottom: none; }
.ai-section-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 14px; cursor: pointer; user-select: none;
}
.ai-section-header:hover { background: #f8fafc; }
.ai-section-title {
  display: flex; align-items: center;
  font-size: 12px; font-weight: 600; color: #374151;
}
.ai-section-meta { display: flex; align-items: center; gap: 6px; }
.ai-count         { font-size: 10px; font-weight: 600; color: #94a3b8; }
.ai-count--filled { color: #6366f1; }
.ai-no-ins        { font-size: 10px; font-weight: 600; color: #f59e0b; }

.ai-mini-bar-wrap { width: 44px; height: 4px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.ai-mini-bar { height: 100%; border-radius: 4px; transition: width 0.4s ease; }
.ai-mini-bar--demo { background: linear-gradient(90deg, #a5b4fc, #818cf8); }
.ai-mini-bar--ins  { background: linear-gradient(90deg, #7dd3fc, #38bdf8); }
.ai-mini-bar--clin { background: linear-gradient(90deg, #6ee7b7, #34d399); }

/* ── Fields ──────────────────────────────────────────────────── */
.ai-fields { padding: 2px 14px 10px 14px; }
.ai-field-row { display: flex; align-items: center; gap: 6px; padding: 3px 4px; border-radius: 6px; }
.ai-field-row--collected .ai-field-label { color: #1e293b; }
.ai-field-icon  { flex-shrink: 0; }
.ai-field-label { font-size: 12px; color: #94a3b8; flex-shrink: 0; min-width: 92px; }
.ai-field-value { font-size: 12px; font-weight: 500; color: #1e293b; word-break: break-all; flex: 1; }
.ai-field-hint  { font-size: 11px; color: #cbd5e1; font-style: italic; flex: 1;
                   animation: blink-hint 2s ease-in-out infinite; }

.ai-no-ins-row { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #92400e; padding: 4px 0; }

/* Dots */
.ai-listen-dot {
  width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
  background: #c7d2fe; animation: blink-dot 1.8s ease-in-out infinite;
}
.ai-listen-dot--ins  { background: #bae6fd; }
.ai-listen-dot--clin { background: #a7f3d0; }

@keyframes blink-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(0.75); }
}
@keyframes blink-hint {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.35; }
}

/* ── Expand transition ───────────────────────────────────────── */
.ai-expand-enter-active, .ai-expand-leave-active {
  transition: max-height 0.25s ease, opacity 0.2s ease;
  overflow: hidden; max-height: 400px;
}
.ai-expand-enter-from, .ai-expand-leave-to { max-height: 0; opacity: 0; }
</style>
